import { LegalLayout } from './LegalLayout';

export function DPAPage() {
  return (
    <LegalLayout title="Data Processing Agreement" lastUpdated="2026-04-22">
      <p className="lead">
        This Data Processing Agreement ("DPA") forms part of the Terms of
        Service between Praxara Ltd ("Processor") and the Customer tenant
        ("Controller") and governs the processing of personal data by Praxara
        on behalf of the Controller. It is compliant with Article 28 of the
        UK GDPR and EU GDPR.
      </p>

      <h2>1. Scope + subject matter</h2>
      <p>
        Praxara processes personal data contained in documents uploaded by the
        Controller (regulatory, clinical, pharmacovigilance) for the purpose of
        providing AI-assisted extraction, review, e-signature, and audit
        services. Categories include patient identifiers (where not
        pseudonymised before upload), health data, adverse event narratives,
        investigator details, and the Controller's own staff identifiers.
      </p>

      <h2>2. Duration</h2>
      <p>For the term of the subscription plus 30 days for export, after which data is irreversibly deleted (see Section 8).</p>

      <h2>3. Processor obligations</h2>
      <ul>
        <li>Process personal data only on documented instructions from the Controller.</li>
        <li>Ensure that staff authorised to process personal data are bound by confidentiality.</li>
        <li>Implement the technical and organisational security measures set out in Annex A.</li>
        <li>Engage sub-processors only as listed in <a href="/legal/subprocessors">the sub-processor list</a> and give the Controller 14 days' notice of any intended change, with a right to object on reasonable grounds.</li>
        <li>Assist the Controller in responding to Data Subject Requests via the Praxara admin UI and, where needed, direct support.</li>
        <li>Notify the Controller of a personal data breach without undue delay and in any event within 48 hours.</li>
        <li>Support the Controller with Data Protection Impact Assessments where the Controller reasonably requests.</li>
      </ul>

      <h2>4. International transfers</h2>
      <p>
        Where processing involves transfer outside the UK/EEA, such transfers
        are governed by the EU Commission's Standard Contractual Clauses
        (2021/914/EU) and, for UK-originating data, the ICO's International
        Data Transfer Addendum. Transfer Impact Assessments are maintained
        for each sub-processor and available on request.
      </p>

      <h2>5. Deletion or return of data</h2>
      <p>
        On termination, the Controller may export all data via API for up to
        30 days. At the end of the 30-day window the Processor will
        permanently delete all Controller personal data from live systems
        within 7 days and from backups within 35 days (the backup retention
        window). A certificate of destruction is available on request.
      </p>

      <h2>6. Audits</h2>
      <p>
        The Controller may, on 30 days' notice and no more than once per
        12 months (except in case of a personal data breach), request evidence
        of compliance in the form of: (a) current ISO 27001 audit report
        (when issued), (b) SOC 2 Type II report (when issued), (c) Praxara's
        self-assessment. On-site audits are permitted for Enterprise-tier
        customers and reasonable, non-disruptive audits by a regulator.
      </p>

      <h2>7. AI model training</h2>
      <p>
        <strong>Praxara does not use Controller data to train general-purpose
        AI models.</strong> Controller data is sent to third-party model
        providers (see sub-processor list) solely to fulfil the immediate
        inference request and is not retained by those providers beyond their
        transient processing — subject to each provider's enterprise data
        handling terms, which we negotiate to exclude training use.
      </p>

      <h2>8. Governing law</h2>
      <p>
        This DPA is governed by the laws of England and Wales.
      </p>

      <h2>Annex A — Security measures</h2>
      <ul>
        <li>Encryption at rest: AES-256 on all databases and blob storage.</li>
        <li>Encryption in transit: TLS 1.2 minimum, TLS 1.3 preferred, HSTS enforced.</li>
        <li>Access control: role-based (ADMIN / MANAGER / REVIEWER / OPERATOR / VIEWER) plus tenant isolation enforced at the application and, for high-risk tables, at the database row-level.</li>
        <li>Multi-factor authentication available and configurable as mandatory by tenant.</li>
        <li>E-signatures compliant with 21 CFR Part 11 (password + reason + timestamp + non-repudiation hash).</li>
        <li>Tamper-evident audit log with SHA-256 chaining, verification endpoint.</li>
        <li>Backups: 35-day rolling, geo-redundant, checksum-verified, restore drills quarterly.</li>
        <li>Incident response: 24-hour triage SLA, breach notification within 48 hours.</li>
        <li>Vulnerability management: Dependabot + npm audit + quarterly penetration testing (from 2026).</li>
        <li>Change management: all code changes via peer-reviewed PR with CI-enforced tests + typecheck.</li>
      </ul>
    </LegalLayout>
  );
}
